import { isAcceptedWorkspaceUploadFile } from './upload-file-policy';

/**
 * Plain workspace storage (My Drive, member folders, Drives) and the org-chat
 * composer keep any file (#5823). Avatars, brand logos, product images and
 * other feature uploads that process a file keep the upload allow-list.
 *
 * A file outside the inline-safe allow-list, and every web page, SVG or XML
 * file, is stored opaque: its object and registry type are
 * `application/octet-stream`, never a claimed `text/html`, SVG or script
 * type. Delivery still keys on the allow-list
 * (`workspaceObjectDeliveryHeaders`), so an opaque file, and any `.html`,
 * `.svg` or script name, is always served as a download.
 */
export const OPAQUE_UPLOAD_MIME_TYPE = 'application/octet-stream';

function normalizedMimeType(type: string): string {
  return type.trim().toLowerCase().split(';', 1)[0]?.trim() ?? '';
}

/** The type to upload and register a plain-storage file with. */
export function storableWorkspaceUploadMimeType(file: {
  name: string;
  type: string;
}): string {
  return !isActiveWebContentUpload(file) && isAcceptedWorkspaceUploadFile(file)
    ? file.type
    : OPAQUE_UPLOAD_MIME_TYPE;
}

/**
 * Active web content (pages, SVG, XML). Since the HTML renderers were closed
 * (#5892, #5856) plain storage keeps it, but only opaque: an active type is
 * never recorded, so such a file is only ever a download. Markup inside a
 * file stored with an inline type is refused by sniffing the stored bytes
 * at completion (convex/media/workspace_upload_active_content.helpers.ts).
 */
const ACTIVE_WEB_CONTENT_FILENAME = /\.(?:html?|xhtml?|svgz?|xml|xsl|xslt)$/i;
const ACTIVE_WEB_CONTENT_MIME =
  /^(?:text\/html|application\/xhtml\+xml|image\/svg\+xml|text\/xml|application\/xml|text\/xsl|application\/xslt\+xml)$/;

export function isActiveWebContentUpload(file: {
  name: string;
  type: string;
}): boolean {
  return (
    ACTIVE_WEB_CONTENT_FILENAME.test(file.name.trim()) ||
    ACTIVE_WEB_CONTENT_MIME.test(normalizedMimeType(file.type))
  );
}

/**
 * Server admission for plain storage: declared opaque (any name, web pages
 * included), or allow-listed. An active type claim is never admitted, nor an
 * active name under anything but the opaque type.
 */
export function isStorableWorkspaceUploadFile(file: {
  name: string;
  type: string;
}): boolean {
  const type = normalizedMimeType(file.type);
  if (ACTIVE_WEB_CONTENT_MIME.test(type)) return false;
  if (type === OPAQUE_UPLOAD_MIME_TYPE) return true;
  return !isActiveWebContentUpload(file) && isAcceptedWorkspaceUploadFile(file);
}

/**
 * Composer registration. Opaque bytes are the any-file path. An allow-listed
 * type (including an SVG a preset drawer still declares) stays admissible.
 */
export function isComposerUploadFile(file: {
  name: string;
  type: string;
}): boolean {
  return (
    isStorableWorkspaceUploadFile(file) || isAcceptedWorkspaceUploadFile(file)
  );
}
